import { useMemo, useState } from "react";
import { CaretDown, CaretRight, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const formatGateDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
};

export default function QuarantineRail({ candidates, onPromote, onDismiss, busyId }) {
  const [expanded, setExpanded] = useState(false);
  const count = candidates.length;
  const hasCandidates = count > 0;
  const activeId = useMemo(() => (busyId ? String(busyId) : ""), [busyId]);

  return (
    <section
      data-testid="quarantine-rail"
      className="mt-6 rounded-[1rem] border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/35 px-5 py-5"
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="rounded-full bg-[#f5ecd9] p-2 text-[#9a6f2b]">
          <ShieldCheck size={18} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
            Unknown senders
          </p>
          <h2 className="mt-1 font-serif text-[1rem] text-[hsl(var(--foreground))]">Quarantine review</h2>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
            Review unfamiliar senders here so the main desk stays focused on obligations already in play.
          </p>
        </div>
        {hasCandidates ? (
          <Button
            type="button"
            variant="outline"
            data-testid="quarantine-rail-toggle"
            className="rounded-[0.75rem] border-[hsl(var(--input))] bg-card"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
            <span>{expanded ? "Hide review" : "Review held mail"}</span>
            <span
              data-testid="quarantine-rail-count"
              className="rounded-full bg-[#f5ecd9] px-2 py-0.5 text-[11px] font-semibold text-[#9a6f2b]"
            >
              {count}
            </span>
          </Button>
        ) : (
          <span
            data-testid="quarantine-rail-count"
            className="inline-flex rounded-full bg-[#ecebe6] px-2.5 py-1 text-[11.5px] text-[#6b7384]"
          >
            0 waiting
          </span>
        )}
      </div>

      {!hasCandidates ? (
        <div
          data-testid="quarantine-empty"
          className="mt-4 rounded-[0.875rem] border border-dashed border-[hsl(var(--border))] bg-card px-4 py-4 text-sm text-[hsl(var(--muted-foreground))]"
        >
          Unknown-sender mail waits here for review and is never auto-deleted.
        </div>
      ) : expanded ? (
        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          {candidates.map((candidate) => {
            const isPromoting = activeId === `${candidate.id}:candidate-promote`;
            const isDismissing = activeId === `${candidate.id}:candidate-dismiss`;
            const isBusy = isPromoting || isDismissing;

            return (
              <article
                key={candidate.id}
                className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-4 py-4"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f5ecd9] px-2.5 py-1 text-[11.5px] text-[#9a6f2b]">
                    <WarningCircle size={12} weight="fill" />
                    {candidate.classification}
                  </span>
                  {candidate.gateDate ? (
                    <span className="inline-flex rounded-full border border-[hsl(var(--input))] px-2.5 py-1 text-[11px] text-[hsl(var(--muted-foreground))]">
                      Gate {formatGateDate(candidate.gateDate)}
                    </span>
                  ) : null}
                </div>
                <p className="mt-3 break-all font-mono text-[12.5px] text-[hsl(var(--foreground))]">
                  {candidate.senderEmail || "Sender address unavailable"}
                </p>
                <h3 className="mt-2 font-serif text-[0.98rem] text-[hsl(var(--foreground))]">{candidate.title}</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    data-testid="quarantine-promote"
                    className="rounded-[0.625rem]"
                    disabled={isBusy}
                    onClick={() => onPromote(candidate.id)}
                  >
                    {isPromoting ? "Promoting…" : "Admit sender & promote"}
                  </Button>
                  <Button
                    type="button"
                    data-testid="quarantine-dismiss"
                    variant="outline"
                    className="rounded-[0.625rem] border-[hsl(var(--input))]"
                    disabled={isBusy}
                    onClick={() => onDismiss(candidate.id)}
                  >
                    {isDismissing ? "Dismissing…" : "Dismiss"}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
